import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MonitorPlay, Loader2, XCircle, Activity, Maximize2, Minimize2, X, Terminal, FolderOpen, ChevronRight, ChevronLeft, GripVertical } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { toast } from 'sonner';
import GuacamoleDisplay from '../components/GuacamoleDisplay';
import RemoteTerminalTab from '../components/asset-tabs/RemoteTerminalTab';
import FileExplorerTab from '../components/asset-tabs/FileExplorerTab';

const RemoteControlViewer = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Side panel states
  const [terminalPanelOpen, setTerminalPanelOpen] = useState(false);
  const [fileExplorerPanelOpen, setFileExplorerPanelOpen] = useState(false);
  const [panelWidth, setPanelWidth] = useState(500);
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    loadSessionInfo();
  }, [sessionId]);

  const loadSessionInfo = async () => {
    try {
      const response = await api.get(`/remote-control/sessions`);
      const sessions = response.data.sessions || [];
      const currentSession = sessions.find(s => s.id === sessionId);
      
      if (currentSession) {
        setSession(currentSession);
        
        // Check if session is still active
        if (currentSession.status !== 'active') {
          setError('This session has ended');
        }
      } else {
        setError('Session not found');
      }
    } catch (err) {
      console.error('Failed to load session:', err);
      setError('Failed to load session information');
    } finally {
      setLoading(false);
    }
  };

  const handleEndSession = async () => {
    if (!session) return;

    try {
      await api.post(`/agents/${session.agent_id}/remote-control/stop?session_id=${sessionId}`);
      toast.success('Session ended successfully');
      window.close();
    } catch (error) {
      console.error('Failed to end session:', error);
      toast.error('Failed to end session');
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Toggle terminal panel
  const toggleTerminalPanel = () => {
    if (fileExplorerPanelOpen) {
      setFileExplorerPanelOpen(false);
    }
    setTerminalPanelOpen(!terminalPanelOpen);
  };

  // Toggle file explorer panel
  const toggleFileExplorerPanel = () => {
    if (terminalPanelOpen) {
      setTerminalPanelOpen(false);
    }
    setFileExplorerPanelOpen(!fileExplorerPanelOpen);
  };

  // Close any open panel
  const closePanel = () => {
    setTerminalPanelOpen(false);
    setFileExplorerPanelOpen(false);
  };

  // Handle panel resize
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const newWidth = window.innerWidth - e.clientX;
      // Clamp between 400 and 800px
      setPanelWidth(Math.min(Math.max(newWidth, 400), 800));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const isPanelOpen = terminalPanelOpen || fileExplorerPanelOpen;

  if (loading) {
    return (
      <div className="h-screen w-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-white text-lg">Establishing connection...</p>
          <p className="text-gray-400 text-sm mt-2">Please wait while we connect to the remote desktop</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-white text-2xl font-bold mb-2">Connection Failed</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button onClick={() => window.close()} variant="outline">
            Close Window
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-gray-900 flex flex-col overflow-hidden">
      {/* Control Bar */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-2 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <MonitorPlay className="w-5 h-5 text-blue-500" />
          <div>
            <h1 className="text-white font-semibold text-sm">Remote Control Session</h1>
            <p className="text-gray-400 text-xs">
              Protocol: {session?.protocol?.toUpperCase()} • Status: {session?.status}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={toggleTerminalPanel}
            variant={terminalPanelOpen ? "secondary" : "ghost"}
            size="sm"
            className={`text-white ${terminalPanelOpen ? 'bg-blue-600 hover:bg-blue-700' : 'hover:bg-gray-700'}`}
            title="Toggle Terminal Panel"
          >
            <Terminal className="w-4 h-4 mr-2" />
            Terminal
            {terminalPanelOpen && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
          <Button
            onClick={toggleFileExplorerPanel}
            variant={fileExplorerPanelOpen ? "secondary" : "ghost"}
            size="sm"
            className={`text-white ${fileExplorerPanelOpen ? 'bg-blue-600 hover:bg-blue-700' : 'hover:bg-gray-700'}`}
            title="Toggle File Explorer Panel"
          >
            <FolderOpen className="w-4 h-4 mr-2" />
            Files
            {fileExplorerPanelOpen && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
          <div className="w-px h-6 bg-gray-600 mx-1"></div>
          <Button
            onClick={toggleFullscreen}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-gray-700"
          >
            {isFullscreen ? (
              <>
                <Minimize2 className="w-4 h-4 mr-2" />
                Exit Fullscreen
              </>
            ) : (
              <>
                <Maximize2 className="w-4 h-4 mr-2" />
                Fullscreen
              </>
            )}
          </Button>
          <Button
            onClick={handleEndSession}
            variant="destructive"
            size="sm"
            className="gap-2"
          >
            <X className="w-4 h-4" />
            End Session
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Remote Desktop Viewer Area */}
        <div 
          className="flex-1 bg-black relative transition-all duration-300 ease-in-out"
          style={{ 
            marginRight: isPanelOpen ? `${panelWidth}px` : '0px' 
          }}
        >
          <GuacamoleDisplay
            sessionId={sessionId}
            agentId={session?.agent_id}
            onError={(errorMsg) => {
              console.error('Guacamole error:', errorMsg);
              setError(errorMsg);
            }}
            onDisconnect={() => {
              toast.info('Remote session disconnected');
            }}
          />
        </div>

        {/* Side Panel */}
        <div 
          className={`absolute top-0 right-0 h-full bg-white shadow-xl border-l border-gray-200 transition-transform duration-300 ease-in-out flex ${
            isPanelOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{ width: `${panelWidth}px` }}
        >
          {/* Resize Handle */}
          <div
            className="absolute left-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-blue-500/20 flex items-center justify-center group"
            onMouseDown={handleMouseDown}
          >
            <div className="w-1 h-12 bg-gray-300 rounded group-hover:bg-blue-500 transition-colors">
              <GripVertical className="w-1 h-12 text-gray-400 group-hover:text-blue-500" />
            </div>
          </div>

          {/* Panel Content */}
          <div className="flex-1 flex flex-col overflow-hidden ml-2">
            {/* Panel Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                {terminalPanelOpen ? (
                  <>
                    <Terminal className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-gray-800">Terminal</span>
                  </>
                ) : (
                  <>
                    <FolderOpen className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-gray-800">File Explorer</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Quick switch buttons */}
                <Button
                  onClick={toggleTerminalPanel}
                  variant={terminalPanelOpen ? "default" : "ghost"}
                  size="sm"
                  className="h-8"
                  title="Switch to Terminal"
                >
                  <Terminal className="w-4 h-4" />
                </Button>
                <Button
                  onClick={toggleFileExplorerPanel}
                  variant={fileExplorerPanelOpen ? "default" : "ghost"}
                  size="sm"
                  className="h-8"
                  title="Switch to File Explorer"
                >
                  <FolderOpen className="w-4 h-4" />
                </Button>
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                <Button
                  onClick={closePanel}
                  variant="ghost"
                  size="sm"
                  className="h-8 text-gray-500 hover:text-gray-700"
                  title="Close Panel"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Panel Body */}
            <div className="flex-1 overflow-auto p-4 bg-gray-50">
              {terminalPanelOpen && session?.agent_id && (
                <RemoteTerminalTab agentId={session.agent_id} />
              )}
              {fileExplorerPanelOpen && session?.agent_id && (
                <FileExplorerTab agentId={session.agent_id} />
              )}
            </div>
          </div>
        </div>

        {/* Collapsed Panel Indicator (when panel is closed) */}
        {!isPanelOpen && (
          <div className="absolute right-0 top-1/2 -translate-y-1/2 flex flex-col gap-2 bg-gray-800/80 rounded-l-lg p-2 backdrop-blur-sm">
            <Button
              onClick={toggleTerminalPanel}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-gray-700 h-10 w-10 p-0"
              title="Open Terminal Panel"
            >
              <Terminal className="w-5 h-5" />
            </Button>
            <Button
              onClick={toggleFileExplorerPanel}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-gray-700 h-10 w-10 p-0"
              title="Open File Explorer Panel"
            >
              <FolderOpen className="w-5 h-5" />
            </Button>
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="bg-gray-800 border-t border-gray-700 px-4 py-1 flex items-center justify-between text-xs shrink-0">
        <div className="flex items-center gap-4 text-gray-400">
          <span>Session: {sessionId?.substring(0, 8)}...</span>
          <span>•</span>
          <span>Connected via: {session?.protocol?.toUpperCase()}</span>
          {isPanelOpen && (
            <>
              <span>•</span>
              <span className="text-blue-400">
                {terminalPanelOpen ? 'Terminal' : 'File Explorer'} Panel Open
              </span>
            </>
          )}
        </div>
        <div className="text-gray-500">
          {session?.status === 'active' ? (
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              Active
            </span>
          ) : (
            <span>Inactive</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default RemoteControlViewer;
